/**
 * Soft pre-cap warning. Shows above the main content area when a
 * user is at 80%+ of their active gating window.
 *
 * Two windows fire this banner:
 * - `daily` (free post-25-signals): "1 coach response left today"
 * - `monthly` (Premium subscriber): "200 coach responses left this month"
 *
 * Dismissible per-window-instance via localStorage. The dismissal key
 * combines the window type + period boundary (day or month) so a
 * user who dismisses for May 27 sees the banner again on May 28
 * (daily) or June 1 (monthly).
 *
 * Sits alongside TopLeakBanner. Both can show simultaneously; each
 * dismissed independently.
 */

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { getBillingStatus, isNearLimit } from "@/features/billing/lib/billing";
import { ICON_STROKE, X } from "@/shared/components/icons";

const STORAGE_KEY = "pt:near-cap-dismissed";

/** Period key for the current cap window. For daily caps: "YYYY-MM-DD".
 *  For monthly caps: "YYYY-MM". Cap-period transitions invalidate the
 *  dismissal so the banner can re-show. */
function currentPeriodKey(window: "daily" | "monthly", now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  if (window === "daily") {
    const d = String(now.getDate()).padStart(2, "0");
    return `daily:${y}-${m}-${d}`;
  }
  return `monthly:${y}-${m}`;
}

function readDismissed(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeDismissed(key: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, key);
  } catch {
    // Storage unavailable — banner just shows again next time.
  }
}

export function NearCapBanner() {
  const { data: status } = useQuery({
    queryKey: ["billing-status"],
    queryFn: getBillingStatus,
    staleTime: 60_000,
  });

  const [dismissed, setDismissed] = useState<string | null>(() => readDismissed());

  // Period rollover: if the stored key doesn't match the current
  // period, treat as not-dismissed (don't suppress the banner past
  // a midnight or month-1st transition).
  const currentKey =
    status && status.gatingWindow !== "none"
      ? currentPeriodKey(status.gatingWindow)
      : null;

  useEffect(() => {
    if (dismissed && currentKey && dismissed !== currentKey) {
      setDismissed(null);
    }
  }, [dismissed, currentKey]);

  if (!status || !isNearLimit(status) || !currentKey) return null;
  if (dismissed === currentKey) return null;

  const proximityLabel =
    status.gatingWindow === "daily"
      ? "Close to today's drill cap."
      : "Close to this month's drill cap.";

  function onDismiss() {
    if (!currentKey) return;
    writeDismissed(currentKey);
    setDismissed(currentKey);
  }

  return (
    <div className="px-4 md:px-8 pt-3">
      <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 flex items-center justify-between gap-3">
        <div className="text-sm text-amber-200/90">
          <span className="font-medium">{proximityLabel}</span>{" "}
          <Link
            to="/pricing"
            className="text-amber-300 underline underline-offset-2 hover:no-underline font-medium"
          >
            Upgrade
          </Link>{" "}
          to drill without limits.
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="w-10 h-10 flex items-center justify-center text-amber-300/60 hover:text-amber-200 shrink-0"
        >
          <X size={14} strokeWidth={ICON_STROKE} />
        </button>
      </div>
    </div>
  );
}
